import React, { useState } from 'react';

interface TestCase {
  id: string;
  name: string;
  description: string;
  run: () => Promise<{ success: boolean; message: string; data?: any }>;
}

interface TestResult {
  id: string;
  status: 'pending' | 'running' | 'success' | 'error';
  message: string;
  duration?: number;
  data?: any;
}

interface TestPanelConfig {
  title: string;
  description: string;
  tests: TestCase[];
  apiEndpoint?: string;
}

export function createTestPanel(config: TestPanelConfig): React.FC {
  return function TestPanel() {
    const [results, setResults] = useState<TestResult[]>([]);
    const [isRunning, setIsRunning] = useState(false);

    const runTests = async () => {
      setIsRunning(true);
      const newResults: TestResult[] = [];

      for (const test of config.tests) {
        // Update to show running
        setResults([...newResults, {
          id: test.id,
          status: 'running',
          message: test.description
        }]);

        const startTime = Date.now();
        try {
          const result = await test.run();
          newResults.push({
            id: test.id,
            status: result.success ? 'success' : 'error',
            message: result.message,
            duration: Date.now() - startTime,
            data: result.data
          });
        } catch (error) {
          newResults.push({
            id: test.id,
            status: 'error',
            message: error.message,
            duration: Date.now() - startTime
          });
        }

        setResults([...newResults]);
      }

      setIsRunning(false);
    };

    const getStatusEmoji = (status: string) => {
      switch (status) {
        case 'success': return '✅';
        case 'error': return '❌';
        case 'running': return '🔄';
        default: return '⏳';
      }
    };

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'success': return 'text-green-500';
        case 'error': return 'text-red-500';
        case 'running': return 'text-yellow-500';
        default: return 'text-gray-400';
      }
    };

    return (
      <div className="min-h-screen bg-gray-900 text-white p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800 rounded-xl shadow-2xl p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-2xl font-bold">{config.title}</h1>
                <p className="text-gray-400">{config.description}</p>
              </div>
              <button
                onClick={runTests}
                disabled={isRunning}
                className={`px-6 py-2 rounded-lg font-bold transition-all ${
                  isRunning
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isRunning ? '⚡ Running...' : '🚀 Run Tests'}
              </button>
            </div>

            <div className="space-y-3">
              {config.tests.map((test) => {
                const result = results.find(r => r.id === test.id);
                const status = result?.status || 'pending';

                return (
                  <div
                    key={test.id}
                    className="bg-gray-700 rounded-lg p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{getStatusEmoji(status)}</span>
                      <div>
                        <div className="font-semibold">{test.name}</div>
                        <div className={`text-sm ${getStatusColor(status)}`}>
                          {result?.message || test.description}
                          {result?.duration && ` (${result.duration}ms)`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {results.length > 0 && !isRunning && (
              <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                <div className="flex justify-around text-sm">
                  <div>
                    ✅ Passed: {results.filter(r => r.status === 'success').length}
                  </div>
                  <div>
                    ❌ Failed: {results.filter(r => r.status === 'error').length}
                  </div>
                  <div>
                    ⏱️ Total: {results.reduce((sum, r) => sum + (r.duration || 0), 0)}ms
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
}

// Example usage for any feature:
export function createFeatureTests(featureName: string, apiUrl: string) {
  return {
    // CRUD Tests
    crud: [
      {
        id: 'create',
        name: `Create ${featureName}`,
        description: `Testing ${featureName} creation...`,
        run: async () => {
          const response = await fetch(`${apiUrl}/api/${featureName}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ test: true })
          });
          return {
            success: response.ok,
            message: response.ok ? 'Created successfully' : 'Creation failed',
            data: await response.json()
          };
        }
      },
      {
        id: 'read',
        name: `Read ${featureName}`,
        description: `Testing ${featureName} retrieval...`,
        run: async () => {
          const response = await fetch(`${apiUrl}/api/${featureName}`);
          return {
            success: response.ok,
            message: response.ok ? 'Retrieved successfully' : 'Retrieval failed',
            data: await response.json()
          };
        }
      },
      {
        id: 'update',
        name: `Update ${featureName}`,
        description: `Testing ${featureName} updates...`,
        run: async () => {
          const response = await fetch(`${apiUrl}/api/${featureName}/1`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updated: true })
          });
          return {
            success: response.ok,
            message: response.ok ? 'Updated successfully' : 'Update failed',
            data: await response.json()
          };
        }
      },
      {
        id: 'delete',
        name: `Delete ${featureName}`,
        description: `Testing ${featureName} deletion...`,
        run: async () => {
          const response = await fetch(`${apiUrl}/api/${featureName}/1`, {
            method: 'DELETE'
          });
          return {
            success: response.ok,
            message: response.ok ? 'Deleted successfully' : 'Deletion failed'
          };
        }
      }
    ],

    // State Tests
    state: [
      {
        id: 'initial-state',
        name: 'Initial State',
        description: 'Checking initial component state...',
        run: async () => {
          // Mock state test
          return {
            success: true,
            message: 'Initial state is correct',
            data: { initialized: true }
          };
        }
      },
      {
        id: 'state-update',
        name: 'State Update',
        description: 'Testing state mutations...',
        run: async () => {
          // Mock state update test
          return {
            success: true,
            message: 'State updates correctly',
            data: { updated: true }
          };
        }
      }
    ],

    // Integration Tests
    integration: [
      {
        id: 'end-to-end',
        name: 'End-to-End Flow',
        description: 'Testing complete user flow...',
        run: async () => {
          // Mock E2E test
          await new Promise(resolve => setTimeout(resolve, 500));
          return {
            success: true,
            message: 'Flow completed successfully',
            data: { steps: 5, passed: 5 }
          };
        }
      }
    ]
  };
}